import React from 'react';
import { Card, Col, Empty, Row, Space, Typography } from 'antd';
import type { FtueProgressionResponse, FtueProgressionView, FtueSection } from '@/types';
import { FtueFunnelTable } from './FtueFunnelTable';
import { LevelLadder } from './LevelLadder';
import { ProgressionTiles } from './ProgressionTiles';
import { filterLabel } from './ftueFormat';

const { Text, Title } = Typography;

interface PartProps {
  name: 'funnel' | 'tiles' | 'ladder';
  title: string;
  note: string;
  failed: boolean;
  children: React.ReactNode;
}

/** One board section; a failed part says so in place of its content. */
const Part: React.FC<PartProps> = ({ name, title, note, failed, children }) => (
  <div data-section={name}>
    <Card size="small" title={title}>
      {failed ? (
        <Text type="secondary">
          This part couldn&apos;t load. It tries again in about 2 minutes.
        </Text>
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
            {note}
          </Text>
          {children}
        </>
      )}
    </Card>
  </div>
);

interface ViewProps {
  view: FtueProgressionView;
  minPlayers: number;
  funnelFailed: boolean;
  progressionFailed: boolean;
}

/** The funnel, tiles and ladder of one view, or a single "no players" message. */
const FtueView: React.FC<ViewProps> = ({ view, minPlayers, funnelFailed, progressionFailed }) => {
  if (!funnelFailed && view.installs === 0) {
    return (
      <Card size="small">
        <Empty description="No players in this filter" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Part
        name="funnel"
        title="1 · Day-0 FTUE funnel: first launch to Tutorial done"
        note="Steps that lose 10% or more of the step before are in red."
        failed={funnelFailed}
      >
        <FtueFunnelTable view={view} minPlayers={minPlayers} />
      </Part>
      <Part
        name="tiles"
        title="2 · After the tutorial: unlocking Custom Match (Level 2) and Blitz (Level 5)"
        note="Base: players who finished the tutorial within 7 days of install (not the funnel's 24 h Tutorial done). Matches are finished matches after the tutorial."
        failed={progressionFailed}
      >
        <ProgressionTiles progression={view.progression} minPlayers={minPlayers} />
      </Part>
      <Part
        name="ladder"
        title="3 · Level ladder: where should Blitz unlock?"
        note="One row per level, to see what moving Blitz up or down a level would mean for matches needed and reach."
        failed={progressionFailed}
      >
        <LevelLadder progression={view.progression} minPlayers={minPlayers} />
      </Part>
    </Space>
  );
};

/** The FTUE & Progression page below its filter bar; pure render of one response. */
export const FtueProgressionBoard: React.FC<{ data: FtueProgressionResponse }> = ({ data }) => {
  const failed = (section: FtueSection) => data.unavailable.includes(section);
  const main = (
    <FtueView
      view={data.view}
      minPlayers={data.minPlayers}
      funnelFailed={failed('funnel')}
      progressionFailed={failed('progression')}
    />
  );
  if (!data.compare) return <div data-view="main">{main}</div>;
  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} xl={12} data-view="main">
        <Title level={5}>{filterLabel(data.view.filter)}</Title>
        {main}
      </Col>
      <Col xs={24} xl={12} data-view="compare">
        <Title level={5}>{filterLabel(data.compare.filter)}</Title>
        <FtueView
          view={data.compare}
          minPlayers={data.minPlayers}
          funnelFailed={failed('compare-funnel')}
          progressionFailed={failed('compare-progression')}
        />
      </Col>
    </Row>
  );
};
